import type { InferContractRouterOutputs } from '@orpc/contract'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { uniqBy } from 'lodash-es'
import type { baseContract } from 'pixai-contract/api/orpc'
import { useMemo } from 'react'
import { orpc } from './contract'
import {
  type OrpcArtworkPreviewFragment,
  toArtworkPreviewFragment,
} from './utils/typeAdapters'

type SimilarArtworksPage = InferContractRouterOutputs<
  typeof baseContract
>['artwork']['getSimilarArtworks']

const getNextPageParam = (lastPage: SimilarArtworksPage) => {
  if (!lastPage.pageInfo.hasNextPage) return undefined
  return lastPage.pageInfo.endCursor
}

interface SimilarArtworksResult {
  artworks: OrpcArtworkPreviewFragment[]
  pageInfo: {
    hasNextPage: boolean
    endCursor?: string
  }
  loading: boolean
  loadingMore: boolean
  fetchMore: () => void
}

/**
 * Infinite-scroll variant — used by the dedicated `/artwork/{id}/relevant` page.
 */
export function useOrpcSimilarArtworksInfinite(
  args: { artworkId: string; first?: number } | null,
): SimilarArtworksResult {
  const enabled = args != null
  const artworkId = args?.artworkId ?? ''
  const first = args?.first

  const result = useInfiniteQuery({
    ...orpc.artwork.getSimilarArtworks.infiniteOptions({
      input: (pageParam: string | undefined) => ({
        artworkId,
        first,
        after: pageParam,
      }),
      getNextPageParam,
      initialPageParam: undefined as string | undefined,
    }),
    enabled,
    // `liked` depends on the current user — drop cache across login/logout.
    meta: { dependsOn: ['login'] },
  })

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    result

  const artworks = useMemo(
    () =>
      uniqBy(
        data?.pages.flatMap(page =>
          page.data.map(item => toArtworkPreviewFragment(item)),
        ) ?? [],
        'id',
      ),
    [data?.pages],
  )
  const lastPage = data?.pages.at(-1)

  return {
    artworks,
    pageInfo: {
      hasNextPage: hasNextPage ?? false,
      endCursor: lastPage?.pageInfo.endCursor,
    },
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    fetchMore: fetchNextPage,
  }
}

interface SingleSimilarArtworksResult {
  artworks: OrpcArtworkPreviewFragment[]
  loading: boolean
}

/**
 * Single-page variant — used by the inline "similar images" sections on the
 * artwork detail page where only the top N items are shown without paging.
 */
export function useOrpcSimilarArtworks(args: {
  artworkId: string
  first?: number
}): SingleSimilarArtworksResult {
  const result = useQuery({
    ...orpc.artwork.getSimilarArtworks.queryOptions({
      input: { artworkId: args.artworkId, first: args.first },
    }),
    meta: { dependsOn: ['login'] },
  })

  const artworks = useMemo(
    () => result.data?.data.map(item => toArtworkPreviewFragment(item)) ?? [],
    [result.data],
  )

  return { artworks, loading: result.isLoading }
}
